<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'

    import { DateTime } from 'luxon'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { asAny, spath } from 'hydria/svelte_helpers'
    import { numberToVisual } from 'hydria/utils/numbers'

    import { renderedTemplate } from 'wdom/helpers/rendered_template'
    import { flashStream } from 'wdom/services/stream'
    import { cardData } from 'wdom/utils/credit_card'
    import type { ICardData } from 'wdom/utils/credit_card'
    import { Breadcrumb } from 'breadcrumb'

    import type { GenOrder, IAccountingMessage, IItem, IOrder, ITrx } from './order'

    import Date from 'hydria/components/Date.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import InternationalPhone from 'wdom/components/contacts/InternationalPhone.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Province from 'wdom/components/contacts/Province.svelte'
    import IntCurrency from 'wdom/components/IntCurrency.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const Order = getContext('Order') as GenOrder

    let error = null
    let cardMeta: ICardData | undefined = $state()
    let cardNumber: string | undefined = $state()
    let finalTrx: ITrx | undefined = $state()
    let accountingMessage: string = $state('')
    let adminMessageHtml: string = $state('')

    let hasCategories: boolean = $derived.by(() => {
        if (order == null || !order.items) return false
        for (const item of order.items) {
            if (item.type == 'RENEWAL' && item.meta['user_domain_category'] != null && item.meta['user_domain_category'].id != null) {
                return true
            }
        }
        return false
    })

    let status = $state()
    unsubs.push(
        Order.status.subscribe(($status) => {
            status = $status
        }),
    )

    // @ts-expect-error
    let items: IItem[] = $state()
    unsubs.push(
        Order.items.subscribe(($items) => {
            items = $items
        }),
    )

    // @ts-expect-error
    let order: IOrder = $state()
    unsubs.push(
        Order.orderStore.subscribe(($order) => {
            order = $order
            if (!order) return
            if (order.accounting_message) {
                let en: IAccountingMessage | undefined
                let found: IAccountingMessage | undefined
                for (const m of order.accounting_message) {
                    if (m.lg == $lg) {
                        found = m
                        break
                    } else if (m.lg == 'en') en = m
                }
                if (!found) found = en
                if (found) accountingMessage = found.text
            }

            tick().then(() => {
                loadFooter($lg, order.billing_address && order.billing_address.country ? order.billing_address.country : 'CA')
            })

            if (finalTrx && finalTrx.payment_method == 'credit card') {
                let card_number_total_digits: number
                let card_number_first_digits: string
                let card_number_last_digits: string
                if (finalTrx.data.processor == 'tpv') {
                    card_number_total_digits = finalTrx.data.tpv.card_number_total_digits
                    card_number_first_digits = finalTrx.data.tpv.card_number_first_digits
                    card_number_last_digits = finalTrx.data.tpv.card_number_last_digits
                } else {
                    if ((finalTrx as any).data && (finalTrx as any).data.query) {
                        card_number_total_digits = (finalTrx as any).data.query.card_number_total_digits
                        card_number_first_digits = (finalTrx as any).data.query.card_number_first_digits
                        card_number_last_digits = (finalTrx as any).data.query.card_number_last_digits
                    } else {
                        card_number_total_digits = 16
                        card_number_first_digits = '0000'
                        card_number_last_digits = '0000'
                    }
                }
                let fakeNum = card_number_first_digits
                // probably mastercards, but not necessarily.. better not to falsely claim it's MC when it might not be, keep commented
                // if (fakeNum == '22') fakeNum += '21'
                // else if (fakeNum == '27') fakeNum += '20'
                fakeNum = fakeNum.padEnd(card_number_total_digits - fakeNum.length - card_number_last_digits.length, '0') + card_number_last_digits
                cardMeta = cardData(fakeNum)
                if (cardMeta == null && card_number_total_digits == 16) {
                    cardMeta = { tag: '-', text: '', innRanges: [], lengths: [16], cvdLength: 3, masks: ['0000 0000 0000 0000'], short: '' }
                }

                cardNumber = undefined
                if (cardMeta) {
                    let i = cardMeta.lengths.indexOf(card_number_total_digits)
                    if (i >= 0) {
                        cardNumber = cardMeta.masks[i].replaceAll('0', 'X')
                        for (const c of card_number_first_digits) {
                            cardNumber = cardNumber.replace('X', c)
                        }
                        for (const c of card_number_last_digits.split('').reverse()) {
                            cardNumber = cardNumber.replace(/(.*)X/, `$1${c}`)
                        }
                    }
                }
                if (!cardNumber) {
                    cardNumber = card_number_first_digits + 'X'.padEnd(card_number_total_digits - card_number_first_digits.length - card_number_last_digits.length, 'X') + card_number_last_digits
                }
            } else {
                cardMeta = undefined
                cardNumber = undefined
            }
        }),
    )

    // @ts-expect-error
    let transactions: ITrx[] = $state()
    unsubs.push(
        Order.transactions.subscribe(($transactions) => {
            transactions = $transactions
            const trxs = transactions.filter((trx) => order.final_transaction && trx.id == order.final_transaction.id)
            finalTrx = trxs.length ? trxs[0] : undefined
        }),
    )

    let footerHtml = $state(null)

    async function loadFooter(lg: string, isoCountryCode: string) {
        footerHtml = await renderedTemplate(lg, 'invoice-footer', { country_code: isoCountryCode })
    }

    const printIcon = '<svg xmlns="http://www.w3.org/2000/svg" style="vertical-align: -0.125em;" width="1.09em" height="1em" preserveAspectRatio="xMidYMid meet" viewBox="0 0 1664 1536"><path fill="currentColor" d="M384 1408h896v-256H384v256zm0-640h896V384h-160q-40 0-68-28t-28-68V128H384v640zm1152 64q0-26-19-45t-45-19t-45 19t-19 45t19 45t45 19t45-19t19-45zm128 0v416q0 13-9.5 22.5t-22.5 9.5h-224v160q0 40-28 68t-68 28H352q-40 0-68-28t-28-68v-160H32q-13 0-22.5-9.5T0 1248V832q0-79 56.5-135.5T192 640h64V96q0-40 28-68t68-28h672q40 0 88 20t76 48l152 152q28 28 48 76t20 88v256h64q79 0 135.5 56.5T1664 832z"/></svg>'

    const crumbs = $derived.by(() => {
        const crs: CrumbArray = [{ txt: tgettext('Orders'), url: spath('/ecom/orders') }]

        if (status == 'ready' || status == 'loading') {
            if (status == 'ready' && order)
                crs.push({
                    txt: order.ctime.toLocaleString(DateTime.DATETIME_MED),
                })
            else if (status == 'ready' || status == 'loading')
                crs.push({
                    txt: '<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />',
                    html: true,
                })
            crs.push({
                txt: `<span class="text-muted">${Order.orderId}</span>`,
                html: true,
            })

            crs.push({
                extra: 'simple',
                txt: tgettext('{icon} Print', { icon: printIcon }),
                html: true,
                urlClasses: 'btn btn-outline-secondary btn-sm',
                button: () => {
                    flashStream(() => {
                        window.print()
                    })
                },
            })
        } else {
            crs.push({
                txt: iconExclamationTriangle,
                html: true,
            })
        }

        return crs
    })
</script>

<style>
    .logo-arc {
        height: 50px;
    }

    .copy-print {
        display: none;
    }
    @media print {
        #inv {
            font-size: 10px;
        }

        .card-print {
            margin-top: 1.5rem;
        }

        .copy-print {
            display: block !important;
            text-align: center;
            font-size: 10px;
        }

        .address :global(p) {
            font-size: 10px !important;
        }

        .card {
            --bs-card-spacer-y: 0.75rem;
            --bs-card-spacer-x: 0.75rem;
            --bs-card-title-font-size: 0.8rem;
        }
    }
    .logo-arc {
        height: 50px;
        /* width: 347px; */
    }

    table .tbody-price tr:not(:first-child) td {
        padding-top: 0;
    }
    table .tbody-price tr:not(:last-child) td {
        padding-bottom: 0;
        border-top: 0;
    }
    table .tbody-price tr td {
        border-top: 0;
    }
    table .tbody-price tr:last-child td {
        border-bottom: 0;
        padding-bottom: 0;
    }

    table .tbody-list-item td {
        /* table .tbody-list-item th, { */
        vertical-align: middle !important;
        /* .cart-item-add-alt {
            font-size: 0.95em;
            font-style: italic;
        } */
    }

    table .col-cat {
        max-width: 16em;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
    table .col-cat.cat-no {
        opacity: 0.5;
        font-style: italic;
    }
    table {
        margin-bottom: 0;
    }
    .card-body > *:last-child {
        margin-bottom: 0;
    }
    .address {
        margin-bottom: 20px;
    }
    .address :global(p) {
        margin-bottom: 5px;
        font-size: 12px;
    }
    .admin-msg {
        white-space: pre-line;
    }
</style>

<Breadcrumb {crumbs} class="d-print-none" />

{#if !error && !order}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else if error}
    <div class="text-danger">{error}</div>
{:else}
    <div id="inv" class="d-grid gap-4">
        <div class="card card-print">
            <div class="card-body">
                <div class="row">
                    <div class="col-sm-12 col-md-5">
                        <img src="/static/img/arc-info-2-logo-colour.svg" alt="A.R.C. Informatique" class="imd-responsive logo-arc" />
                    </div>
                    <div class="col-sm-12 col-md-7 text-end margin-top-xs">
                        <div>
                            <strong>
                                {#if order.status == 'quotation'}
                                    <span>{$_('Quotation ID:')}</span>
                                {:else}
                                    <span>{$_('Order ID:')}</span>
                                {/if}
                            </strong>
                            {order.id}
                        </div>
                        <div>
                            <strong>{$_('Date:')}</strong>
                            <Date value={finalTrx ? finalTrx.stamp : order.ctime} format={'DATETIME_MED'} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row equal g-4">
            <div class="col-sm-6 d-grid">
                <div class="card">
                    <div class="card-title text-primary">{$_('Bill to')}</div>
                    <div class="card-body">
                        {#if order.meta.accounting_comment}
                            <small class="float-end">{order.meta.accounting_comment}</small>
                        {/if}
                        <address>
                            {#if order.billing_address.org}
                                <div><strong>{order.billing_address.org}</strong></div>
                            {/if}
                            {#if order.billing_address.name}
                                <div>{order.billing_address.name}</div>
                            {/if}
                            <div>{order.billing_address.street[0]}</div>
                            {#if order.billing_address.street.length > 1}
                                <div>{order.billing_address.street[1]}</div>
                            {/if}
                            <div>
                                {order.billing_address.city}
                                {#if $lg == 'fr' && order.billing_address.country == 'CA'}(<Province contact={order.billing_address} />){:else}{order.billing_address.sp}{/if}
                                &nbsp;&nbsp; <PostalCode contact={order.billing_address} />
                            </div>
                            {#if order.billing_address.country && order.billing_address.country != 'CA'}<Country contact={order.billing_address} />{/if}
                            {#if order.billing_address.voice}
                                <div class="mt-2"><InternationalPhone phone={order.billing_address.voice} /></div>
                            {/if}
                            {#if order.billing_address.mobile}
                                <div class="mt-2"><InternationalPhone phone={order.billing_address.mobile} /> (mobile)</div>
                            {/if}
                            {#if order.billing_address.email}
                                <div>{order.billing_address.email}</div>
                            {/if}
                        </address>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 d-grid">
                {#if order.status == 'final' || order.admin_message}
                    <div class="card">
                        <div class="card-title text-primary">{$_('Message')}</div>
                        <div class="card-body">
                            {#if accountingMessage && order.status == 'final'}
                                <p>{accountingMessage}</p>
                            {/if}
                            {#if order.admin_message}
                                <p class="admin-msg">{order.admin_message}</p>
                            {/if}
                        </div>
                    </div>
                {/if}
                {#if ['quotation', 'unpaid', 'draft', 'ready'].includes(order.status) && order.user_instructions}
                    <div class="card">
                        <div class="card-title text-primary">{$_('Additional instructions')}</div>
                        <div class="card-body">
                            {#if order.user_instructions}
                                {order.user_instructions}
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
        <div class="card">
            <div class="card-title text-primary">{$_('Order')}</div>
            <div class="card-body">
                <table class="table table-condensed" style="border-bottom: 2px solid #f3f5f6">
                    <thead>
                        <tr>
                            <th>{$_('Description')}</th>
                            {#if hasCategories}
                                <th class="col-cat">{$_('Catégorie')}</th>
                            {/if}
                            <th>{$_('Units')}</th>
                            <th class="text-end">{$_('Price')}</th>
                        </tr>
                    </thead>
                    <tbody class="tbody-list-item">
                        {#each items as item}
                            <tr>
                                <td>{item.desc}</td>
                                {#if hasCategories}
                                    {#if item.meta['user_domain_category'] != null && item.meta['user_domain_category']['id'] != null}
                                        <td class="col-cat cat-yes"><span>{item.meta['user_domain_category']['name']}</span></td>
                                    {:else}
                                        <td class="col-cat cat-no"><span>{$_('Non-catégorisé')}</span></td>
                                    {/if}
                                {/if}
                                <td>{item.qty}</td>
                                <td class="text-end" style="min-width: 70px;"><IntCurrency value={item.value} /></td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
                <table class="table table-condensed" style="max-width: 300px; margin-left: auto">
                    <tbody class="tbody-price">
                        {#if order.taxes.length}
                            <tr>
                                <td><strong>{$_('Subtotal')}</strong></td>
                                <td class="text-end"><strong><IntCurrency value={order.subtotal} /></strong></td>
                            </tr>
                            {#each order.taxes as tax}
                                <tr>
                                    <td>{tax.name} &nbsp;{numberToVisual(tax.pct, { units: '%', nDecimals: 3 })}</td>
                                    <td class="text-end"><IntCurrency value={tax.value} /></td>
                                </tr>
                            {/each}
                            <tr style={order.taxes.length ? 'border-top: 1px solid #f3f5f6' : ''}>
                                <td><strong>{$_('Total')}</strong></td>
                                <td class="text-end"><strong><IntCurrency value={order.total} /></strong></td>
                            </tr>
                            <tr>
                                <td>{$_('Amount paid')}</td>
                                <td class="text-end"><IntCurrency forceNeg={true} zeroIsNeg={true} value={order.status == 'paid' || order.status == 'final' ? order.total : 0} /></td>
                            </tr>
                            <tr>
                                <td><strong>{$_('Amount due')}</strong></td>
                                <td class="text-end"
                                    ><strong>
                                        <IntCurrency value={order.status == 'paid' || order.status == 'final' ? 0 : order.total} />
                                    </strong></td
                                >
                            </tr>
                        {/if}
                    </tbody>
                </table>
            </div>
        </div>
        <div class="row equal g-4">
            <div class="col-sm-6 d-grid">
                {#if order.status == 'final'}
                    <div class="card">
                        <div class="card-title text-primary">{$_('Payment information')}</div>
                        <div class="card-body">
                            {#if finalTrx}
                                {#if finalTrx.payment_method == 'cheque'}
                                    <table width="100%">
                                        <tbody>
                                            <tr>
                                                <th>{$_('Payment method')}</th>
                                                <td>{$_('Cheque')}</td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Payment date')}</th>
                                                <td><Date value={finalTrx.data.cheque.date} format={'yyyy-MM-dd'} /></td>
                                            </tr>
                                            {#if finalTrx.data.cheque.number}
                                                <tr>
                                                    <th>{$_('Cheque number')}</th>
                                                    <td>{finalTrx.data.cheque.number}</td>
                                                </tr>
                                            {/if}
                                            {#if finalTrx.data.cheque.name_on_cheque}
                                                <tr>
                                                    <th>{$_('Name on cheque')}</th>
                                                    <td>{finalTrx.data.cheque.name_on_cheque}</td>
                                                </tr>
                                            {/if}
                                        </tbody>
                                    </table>
                                {:else if finalTrx.payment_method == 'wire transfer'}
                                    <table width="100%">
                                        <tbody>
                                            <tr>
                                                <th>{$_('Payment method')}</th>
                                                <td>{$_('Wire transfer')}</td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Payment date')}</th>
                                                <td><Date value={finalTrx.data.wire.date} format={'yyyy-MM-dd'} /></td>
                                            </tr>
                                            {#if finalTrx.data.wire.confirmation}
                                                <tr>
                                                    <th>{$_('Confirmation code')}</th>
                                                    <td>{finalTrx.data.wire.confirmation}</td>
                                                </tr>
                                            {/if}
                                        </tbody>
                                    </table>
                                {:else if finalTrx.payment_method == 'credit card'}
                                    <table width="100%">
                                        <tbody>
                                            <tr>
                                                <th>{$_('Payment method')}</th>
                                                <td>{cardMeta && cardMeta.text ? $_('Credit Card ({type})', { type: cardMeta.text }) : $_('Credit Card')}</td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Payment date')}</th>
                                                <td><Date value={finalTrx.stamp} format={'yyyy-MM-dd H:mm:ss'} /></td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Name on card')}</th>
                                                <td>{finalTrx.data.processor == 'tpv' ? finalTrx.data.tpv.name_on_card : asAny(finalTrx).data.query.name_on_card}</td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Card number')}</th>
                                                <td>
                                                    {cardNumber}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Card expiration')}</th>
                                                <td>{`${finalTrx.data.processor == 'tpv' ? finalTrx.data.tpv.expiration_month : asAny(finalTrx).data.query.expiration_month}`.padStart(2, '0')}&thinsp;/&thinsp;{finalTrx.data.processor == 'tpv' ? finalTrx.data.tpv.expiration_year : asAny(finalTrx).data.query.expiration_year}</td>
                                            </tr>
                                            <tr>
                                                <th>{$_('Authorization code')}</th>
                                                <td>{finalTrx.data.processor == 'tpv' ? finalTrx.data.tpv.auth_code : asAny(finalTrx).data.response.auth_code}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                {/if}
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
            <div class="col-sm-6 d-grid">
                {#if order.status == 'final'}
                    <div class="card">
                        <div class="card-title text-primary">{$_('Additional instructions')}</div>
                        <div class="card-body">
                            {#if order.user_instructions}
                                <p>{order.user_instructions}</p>
                            {/if}
                            {#if order.meta.quebec_intended_use}
                                <p>{$_('.quebec intended use:')} {order.meta.quebec_intended_use}</p>
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <div class="text-center address mt-4">
        {@html footerHtml}
    </div>
    <hr />

    <div class="copy-print">
        {$_('© {year} A.R.C. Informatique inc.', { year: order.ctime.year })}
    </div>
{/if}
